import moment from 'moment';

export * from './package-listing-text';

import { IProduction, IPerformanceGroup, IPerformance, IPackage, IFacility, IPriceType, IPrice, IZone, IPerformancePricing, IZoneSummary } from '../custom/classes/production';

export interface IOption {
    text: string;
    value: string;
}

export interface IPackageListingService<T extends IProduction> {
    (options: IPackageListingOptions<T>): IPackageListing<T>;
}


export interface IPackageListingOptions<T extends IProduction> {
    weekdayNames?: Array<string>;
}


export interface IPackageListing<T extends IProduction> {
    performanceGroups: Array<IPerformanceGroup>;
}

export interface IBaseProduction {
    productionId: number;
    productionTitle: string;
    pdpUrl: string;
    seasonId: number;
    seasonDescription: string;
    seasonYear: number;
    productionSeasonId: number;
    productionSeasonDescription: string;
    pdpImageSrc: string;
    performances: Array<IPerformance>;

    productionSynopsis: string;
    dateRangeString: string;

    //dayOfWeekOfPeformances: Array<string>;
    //timeSlotOfPeformances: Array<string>

    facility: IFacility;
}


export interface IBasePerformance {
    onSale: boolean;
    date: Date;
    dayOfWeekString: string;
    timeSlotString: string;
    performanceDateString: string;
    performanceTimeString: string;
    performanceDateDayString: string;
    productionDetailPageLink: string;
    performanceId: number;
    productionSeasonId: number;
    title: string;

    facility: IFacility;
    description: string;

    shortName: string;

    ticketText1: string;

    ticketText2: string;

    ticketText3: string;

    ticketText4: string;
    genre: string;
}

export interface IBasePerformanceGroup {

    description: string;

    id: number;


    maximumPerformances: number;

    minimumPerformances: number;

    tessPerformances: Array<IPerformance>;
    appProductions: Array<IProduction>;
}

export interface IBaseFacility {
    facilityDescription: string;
    facilityId: number;
    // need venue config to be created
    facilityImageUrl: string;
}

export enum PackageType {
    Fixed,
    Flex,
    Super
}

export interface ISeasonSummary {
    description: string;
    fYear: number;
    id: number;
    inactive: boolean;
}

export interface IBasePackageRequest {

    packageId: number;
    zoneId: number;
    requestedPriceTypeIds: Array<number>;
    configId: number;
    orderNotes: string;
    subPackageZones: { [subPackageId: number]: number; };

}

export interface IBasePackage {
    title: string;
    description: string;
    useUnseatedReservation: boolean;
    facility: IFacility;
    id: number;
    packageType: PackageType;
    maximumPerformances: number;
    minimumPerformances: number;
    packageDateTime: Date;
    performanceGroupDetails: Array<IPerformanceGroup>;
    numberOfPerformancesMessage: string;
    packageTimeDescriptionMessage: string;
    packageImageUrl: string;

    season: ISeasonSummary;

    subPackages: Array<IPackage>;

    text1: string;
    text2: string;
    text3: string;
    text4: string;

    allowSameProductionReservation: boolean;
    packagePriceTypes: Array<IPriceType>;
}

export interface IBasePriceType {
    priceTypeId: number;
    description: string;
    shortDescription: string;
    defaultIndicator: boolean;
    isBest: string;
    offer: boolean;
    maxSeats: number;
    zones: Array<IZone>;
}

export interface IBasePerformancePricing {

    performanceId: number;
    zonesAvailable: boolean;
    zoneMapId: number;

    prices: Array<IPrice>;
    zones: Array<IZone>;

    priceTypeDetails: Array<IPriceType>;
}

export interface IBasePackagePricing {

    packageId: number;
    zonesAvailable: boolean;
    zoneMapId: number;

    prices: Array<IPrice>;
    zones: Array<IZone>;

    priceTypeDetails: Array<IPriceType>;
}

export interface IBaseZone {
    zone: IZoneSummary;
    sectionSummaries: Array<any>;

    performanceId: number;

    availableCount: number;

    prices: Array<IPrice>;
}

export interface IBaseZoneSummary {
    id: number;

    description: string;

    shortDescription: string;

    rank: string;

    zoneMapId: number;

    zoneTime: string;

    abbreviation: string;

    zoneLegend: string;
}

export interface IBasePrice {
    performanceId: number;
    packageId: number;
    zoneId: number;
    priceTypeId: number;
    price: number;
    enabled: boolean;
    isBase: boolean;
    parentPackageId: number;
    performancePriceTypeId: number;
    minPrice: number;
    isBest: boolean;
    offer: boolean;
    modeOfSaleOfferId: number;
}

export interface IDateRange {
    start: Date;
    end: Date;
}

export interface IPerformanceErrorMessage {
    performanceId: number;
    errorMessage: string;
}

export interface ISpecialFeatureMapValue {
    performanceCount: number;
    symbol: string;
    message: string;
    performances: IPerformance[];
}

export interface IPackageListingState<T extends IPackage> {
    processing: boolean;
    errorMessages: Array<IPerformanceErrorMessage>;
    eventFilter: IEventFilter[];
    filterFunction: (event: IProduction) => boolean;
    //performanceGroups: Array<IPerformanceGroup>;
    packageDetail: IPackage;
    packages: Array<IPackage>;
    subPackagesInCart: Map<number, Array<number>>;
    cartItems: Array<ISfoCartItem>; // package/performance : selected zone id
    priceTypeQuantity: Map<number, number>;
    totalNumberOfTicket: number;
    performancePricingList: Map<number, IPerformancePricing>;
    orderNotes: string;
    seatingPreferences: string;
    miniCartOpen: boolean;
    apiError: any;
    specialFeatureMap: Map<string, ISpecialFeatureMapValue>;
    disableAllSeriesButtons: boolean;
}

export const defaultPackageListingModel: IPackageListingState<IPackage> = {
    processing: false,
    errorMessages: [],
    eventFilter: [],
    filterFunction: () => { return true; },
    cartItems: [],
    packageDetail: null,
    packages: [],
    priceTypeQuantity: new Map<number, number>(),
    apiError: null,
    totalNumberOfTicket: 0,
    performancePricingList: new Map<number, IPerformancePricing>(),
    subPackagesInCart: new Map<number, Array<number>>(),
    orderNotes: null,
    seatingPreferences: "",
    miniCartOpen: false,
    specialFeatureMap: new Map<string, ISpecialFeatureMapValue>(),
    disableAllSeriesButtons: false
};

//export interface IProductionSeasonFilter {
//    productionSeasonId: number;
//    productionName: string;
//}

export interface IEventFilter {
    key: string;
    selectedValue: any;
    filter: (event: IProduction) => boolean;
}

export enum AlternateCartItemType {
    ZONE,
    PACKAGE
}

export interface IAlternateCartItem extends IBaseSfoCartItem {
    type: AlternateCartItemType
}

export interface IBaseSfoCartItem {
    cartItemId: number; // package or performance Id for flex package
    selectedZoneId: number;
}

export interface ISfoCartItem extends IBaseSfoCartItem {
    selectedAlternateItem: IAlternateCartItem | null;
}
